import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter, useRoutes } from 'react-router-dom';
import { ConfigProvider, App as AntApp, Spin, theme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import useUiStore from './store/useUiStore';
import useAuthStore from './store/useAuthStore';
import authService from './services/authService';
import { getLightTheme, getDarkTheme, getCompactTheme } from './utils/tokens';
import routes from './routes';
import { setMessageApi } from './services/api';
import PageLoading from './components/PageLoading';
import ChunkLoadErrorBoundary from './components/ChunkLoadErrorBoundary';
import { consumeReturnUrl } from './utils/returnUrl';
import 'dayjs/locale/zh-cn';

const AppRoutes: React.FC = () => {
  const element = useRoutes(routes);
  // Covers top-level lazy routes (e.g. /login) outside BasicLayout/BlankLayout outlets.
  return (
    <ChunkLoadErrorBoundary>
      <Suspense fallback={<PageLoading fullPage />}>
        {element}
      </Suspense>
    </ChunkLoadErrorBoundary>
  );
};

const AppContent: React.FC = () => {
  const { token } = theme.useToken();
  const { message } = AntApp.useApp();
  const { setUser, setLoading, isLoading } = useAuthStore();
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    setMessageApi(message);
  }, [message]);

  useEffect(() => {
    // 初始化身份验证
    const initAuth = async () => {
      try {
        setLoading(true);
        const user = await authService.getCurrentUser();
        setUser(user);

        // After SAML, restore deep link saved on the login page (if any).
        // Only apply on generic landing paths so a stale value never steals
        // an intentional deep link or in-app navigation.
        const returnUrl = consumeReturnUrl();
        if (returnUrl) {
          const path = window.location.pathname;
          const isLanding =
            path === '/' || path === '/dashboard' || path === '/login';
          const current = path + window.location.search;
          if (isLanding && current !== returnUrl) {
            window.location.replace(returnUrl);
            return;
          }
        }
      } catch (error) {
        console.error('Failed to get current user:', error);
        // 如果获取用户信息失败，可能需要重定向到登录页
        // 这里暂时只是清空用户信息
        setUser(null);
      } finally {
        setLoading(false);
        setAuthChecked(true);
      }
    };

    initAuth();
  }, [setUser, setLoading]);

  // 在身份验证检查完成前显示加载状态
  if (!authChecked || isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: token.colorBgLayout,
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
};

const ThemeDocument: React.FC = () => {
  const { token } = theme.useToken();
  const themeMode = useUiStore((s) => s.themeMode);

  useEffect(() => {
    document.documentElement.style.backgroundColor = token.colorBgLayout;
    document.body.style.backgroundColor = token.colorBgLayout;
    document.documentElement.setAttribute('data-theme', themeMode);
  }, [token.colorBgLayout, themeMode]);

  return null;
};

const App: React.FC = () => {
  const { themeMode, density } = useUiStore();

  const themeConfig = density === 'compact'
    ? getCompactTheme(themeMode === 'dark')
    : themeMode === 'dark'
      ? getDarkTheme()
      : getLightTheme();

  return (
    <ConfigProvider theme={themeConfig} locale={zhCN}>
      <ThemeDocument />
      <AntApp>
        <AppContent />
      </AntApp>
    </ConfigProvider>
  );
};

export default App;